<template>
  <section v-if="contextData">
    <div class="container-wrap py-9 tablet:py-12">
      <div class="mb-10 tablet:flex tablet:items-center">
        <div class="tablet:w-8/12 tablet:pr-2 desktop:pr-3">
          <h2 class="text-h2 mb-6 tablet:line-clamp-2">
            {{ contextData.title }}
          </h2>
          <p class="tablet:line-clamp-7 w-full text-sm leading-6 text-gray-700">
            {{ contextData.description }}
          </p>
        </div>
        <div v-if="!isMobile" class="tablet:w-4/12 tablet:pl-2 desktop:pl-3">
          <div class="flex flex-wrap">
            <TheButton
              v-if="button01?.text"
              :id="button01?.trackingCode"
              :to="provideFormatUrl(button01.link)"
              :title="button01.text"
              class="mb-3 w-full last:mb-0"
            >
              <span class="text-sm font-bold">
                {{ button01.text }}
              </span>
            </TheButton>
            <TheButton
              v-if="button02?.text"
              :id="button02?.trackingCode"
              :to="provideFormatUrl(button02.link)"
              :title="button02.text"
              type="outline"
              class="mb-3 w-full last:mb-0"
            >
              <span class="text-sm font-bold">
                {{ button02.text }}
              </span>
            </TheButton>
          </div>
        </div>
      </div>

      <figure
        class="relative mb-6 block aspect-video w-full overflow-hidden rounded-3xl last:mb-0"
      >
        <img
          :src="contextThumbnail"
          :alt="contextData.title || contextData.description"
          loading="lazy"
          class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
        />
      </figure>

      <div v-if="isMobile" class="flex flex-wrap">
        <TheButton
          v-if="button01?.text"
          :id="button01?.trackingCode"
          :to="provideFormatUrl(button01.link)"
          :title="button01.text"
          class="mb-4 w-full last:mb-0"
        >
          <span class="text-sm font-bold">
            {{ button01.text }}
          </span>
        </TheButton>
        <TheButton
          v-if="button02?.text"
          :id="button02?.trackingCode"
          :to="provideFormatUrl(button02.link)"
          :title="button02.text"
          type="outline"
          class="mb-4 w-full last:mb-0"
        >
          <span class="text-sm font-bold">
            {{ button02.text }}
          </span>
        </TheButton>
      </div>
    </div>
  </section>

  <SectionListText05
    :title="contextData.title"
    :description="contextData.description"
    :button01="button01"
    :button02="button02"
    :thumbnail="contextThumbnail"
  />
</template>

<script setup lang="ts">
  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IButton {
    text: string;
    link: string;
    trackingCode?: string;
  }

  interface IArchitectureData {
    title: string;
    description: string;
    buttons: IButton[];
    thumbnail: string;
  }

  interface IArchitecture {
    component: 'architecture';
    meta: {
      navigable?: string;
    };
    data: IArchitectureData;
  }

  type IProps = ISuiteProps<IArchitecture>;

  const props = defineProps<IProps>();

  const { isMobile } = useScreen();
  const { provideFormatUrl } = useFormatUrl();
  const { provideSuiteThumbnailUrl } = useImagePath(props.urlConnector);

  const contextData = computed((): IArchitectureData => props.suiteData.data);

  const contextThumbnail = computed((): string =>
    provideSuiteThumbnailUrl(contextData.value.thumbnail)
  );

  const button01 = computed((): IButton | undefined => {
    const button = contextData.value.buttons[0];
    return button
      ? { ...button, link: provideFormatUrl(button.link) }
      : undefined;
  });

  const button02 = computed((): IButton | undefined => {
    const button = contextData.value.buttons[1];
    return button
      ? { ...button, link: provideFormatUrl(button.link) }
      : undefined;
  });

  export type { IArchitecture, IArchitectureData, IButton };
</script>
